import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { aciesDateFormat } from '@shared/helpers';
import { GenericTableColumn } from '@shared/models/generictablecolumn';
import { IPolicy, IPolicyMap, IScimGroup, IScimUser } from '@shared/models/scim-admin';
import { NotificationService } from '@shared/services/notification.service';
import { actionDefintion } from '@shared/utils/SimpleActionManager';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { CreatePolicyDialogComponent } from './create-policy-dialog/create-policy-dialog.component';
import { CreatePolicyMapDialogComponent } from './create-policy-map-dialog/create-policy-map-dialog.component';
import { PolicyDetailDialogComponent } from './policy-detail-dialog/policy-detail-dialog.component';
import { PolicyMapDetailDialogComponent } from './policy-map-detail-dialog/policy-map-detail-dialog.component';
import { ScimAdminService } from './scim-admin.service';
import { ScimGroupDetailDialogComponent } from './scim-group-detail-dialog/scim-group-detail-dialog.component';
import { ScimTokenDialogComponent } from './scim-token-dialog/scim-token-dialog.component';
import { ScimUserDetailDialogComponent } from './scim-user-detail-dialog/scim-user-detail-dialog.component';
import { UpdatePolicyDialogComponent } from './update-policy-dialog/update-policy-dialog.component';

type ActionHandler = 'openUserDetail' | 'confirmDeleteUser' | 'openGroupDetail' | 'confirmDeleteGroup' | 'openPolicyMapDetail' | 'confirmDeletePolicyMap' | 'openPolicyDetail' | 'editPolicy' | 'confirmDeletePolicy';

interface IRowAction {
	action: { title: string; handler: ActionHandler };
	data: any;
}

@Component({
	selector: 'app-scim-administration',
	templateUrl: './scim-administration.component.html',
	styleUrls: ['./scim-administration.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ScimAdministrationComponent implements OnInit, OnDestroy {
	private destroy$ = new Subject<void>();

	@ViewChild('searchInput') searchInput: ElementRef;

	selectedTabIndex = 0;
	searchValue = '';

	// Users
	usersTableSource = new MatTableDataSource<any>();
	usersColumns: GenericTableColumn[] = [
		{
			name: 'scim-administration.users.columns.name',
			dataKey: 'fullName',
			isSortable: true,
		},
		{
			name: 'scim-administration.users.columns.email',
			dataKey: 'primaryEmail',
			isSortable: true,
		},
		{
			name: 'scim-administration.users.columns.active',
			dataKey: 'activeIcon',
			isSortable: true,
			isStatusIcon: true,
		},
	];

	usersRowActions: actionDefintion<IScimUser>[] = [
		{
			title: 'scim-administration.users.actions.viewDetails',
			handler: 'openUserDetail',
		},
		{
			title: 'scim-administration.users.actions.delete',
			handler: 'confirmDeleteUser',
		},
	];

	isLoadingUsers = false;

	// Groups
	groupsTableSource = new MatTableDataSource<IScimGroup>();
	groupsColumns: GenericTableColumn[] = [
		{
			name: 'scim-administration.groups.columns.displayName',
			dataKey: 'displayName',
			isSortable: true,
		},
		{
			name: 'scim-administration.groups.columns.members',
			dataKey: 'memberCount',
			isSortable: true,
		},
	];

	groupsRowActions: actionDefintion<IScimGroup>[] = [
		{
			title: 'scim-administration.groups.actions.viewDetails',
			handler: 'openGroupDetail',
		},
		{
			title: 'scim-administration.groups.actions.delete',
			handler: 'confirmDeleteGroup',
		},
	];

	isLoadingGroups = false;

	// Policy Maps
	policyMapsTableSource = new MatTableDataSource<IPolicyMap>();
	policyMapsColumns: GenericTableColumn[] = [
		{
			name: 'scim-administration.policyMaps.columns.groupName',
			dataKey: 'groupName',
			isSortable: true,
		},
		{
			name: 'scim-administration.policyMaps.columns.groupExternalId',
			dataKey: 'groupExternalId',
			isSortable: true,
		},
		{
			name: 'scim-administration.policyMaps.columns.policyName',
			dataKey: 'policyName',
			isSortable: true,
		},
		{
			name: 'scim-administration.policyMaps.columns.policyId',
			dataKey: 'policyId',
			isSortable: true,
		},
		{
			name: 'scim-administration.policyMaps.columns.createdAt',
			dataKey: 'createdAtFormatted',
			sortKey: 'createdAt',
			isSortable: true,
		},
	];

	policyMapsRowActions: actionDefintion<IPolicyMap>[] = [
		{
			title: 'scim-administration.policyMaps.actions.viewDetails',
			handler: 'openPolicyMapDetail',
		},
		{
			title: 'scim-administration.policyMaps.actions.delete',
			handler: 'confirmDeletePolicyMap',
		},
	];

	isLoadingPolicyMaps = false;

	// Policies
	policiesTableSource = new MatTableDataSource<any>();
	policiesColumns: GenericTableColumn[] = [
		{
			name: 'scim-administration.policies.columns.name',
			dataKey: 'name',
			isSortable: true,
		},
		{
			name: 'scim-administration.policies.columns.type',
			dataKey: 'type',
			isSortable: true,
		},
		{
			name: 'scim-administration.policies.columns.description',
			dataKey: 'description',
			isSortable: true,
		},
	];

	policiesRowActions: actionDefintion<IPolicy>[] = [
		{
			title: 'scim-administration.policies.actions.viewDetails',
			handler: 'openPolicyDetail',
		},
		{
			title: 'scim-administration.policies.actions.edit',
			handler: 'editPolicy',
			disableFilter: (row: any) => row.type === 'managed',
		},
		{
			title: 'scim-administration.policies.actions.delete',
			handler: 'confirmDeletePolicy',
			disableFilter: (row: any) => row.type === 'managed',
		},
	];

	isLoadingPolicies = false;

	// Token
	isLoadingToken = false;

	// Cached data for create dialog
	private cachedGroups: IScimGroup[] = [];
	private cachedPolicies: any[] = [];
	isDataReadyForCreatePolicyMap = false;

	constructor(
		public dialog: MatDialog,
		private scimAdminService: ScimAdminService,
		private notificationService: NotificationService,
		private translator: TranslateService,
	) {}

	ngOnInit(): void {
		this.loadActiveTab();
	}

	private loadActiveTab(): void {
		switch (this.selectedTabIndex) {
			case 0:
				this.loadUsers();
				break;
			case 1:
				this.loadGroups();
				break;
			case 2:
				this.loadPolicyMaps();
				break;
			case 3:
				this.loadPolicies();
				break;
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onTabChange(event: MatTabChangeEvent): void {
		this.selectedTabIndex = event.index;
		this.searchValue = '';
		this.usersTableSource.filter = '';
		this.groupsTableSource.filter = '';
		this.policyMapsTableSource.filter = '';
		this.policiesTableSource.filter = '';
		if (this.searchInput) {
			this.searchInput.nativeElement.value = '';
		}
		switch (event.index) {
			case 0:
				this.loadUsers();
				break;
			case 1:
				this.loadGroups();
				break;
			case 2:
				this.loadPolicyMaps();
				break;
			case 3:
				this.loadPolicies();
				break;
		}
	}

	loadUsers(): void {
		this.isLoadingUsers = true;
		this.scimAdminService
			.getUsers()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (users) => {
					this.usersTableSource.data = users.map((user) => ({
						...user,
						fullName: [user.name?.givenName, user.name?.familyName].filter(Boolean).join(' ') || user.displayName || user.userName,
						primaryEmail: user.emails?.find((email) => email.primary)?.value || user.emails?.[0]?.value || '',
						activeIcon: user.active ? 'acies_true' : 'acies_false',
					}));
					this.isLoadingUsers = false;
				},
				error: () => {
					this.isLoadingUsers = false;
					this.notificationService.notify(['scim-administration.errors.loadUsers.header'], ['scim-administration.errors.loadUsers.content'], 'error');
				},
			});
	}

	loadGroups(): void {
		this.isLoadingGroups = true;
		this.scimAdminService
			.getGroups()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (groups) => {
					this.groupsTableSource.data = groups.map((group) => ({
						...group,
						memberCount: group.members?.length || 0,
					}));
					this.isLoadingGroups = false;
				},
				error: () => {
					this.isLoadingGroups = false;
					this.notificationService.notify(['scim-administration.errors.loadGroups.header'], ['scim-administration.errors.loadGroups.content'], 'error');
				},
			});
	}

	loadPolicyMaps(): void {
		this.isLoadingPolicyMaps = true;
		this.isDataReadyForCreatePolicyMap = false;

		// Load policy maps first and show immediately with raw IDs
		this.scimAdminService
			.getPolicyMaps()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (policyMaps) => {
					this.policyMapsTableSource.data = policyMaps.map((policyMap) => ({
						...policyMap,
						groupName: '...',
						policyName: '...',
						createdAtFormatted: policyMap.createdAt ? aciesDateFormat(new Date(policyMap.createdAt), true) : '',
					}));
					this.isLoadingPolicyMaps = false;

					// Resolve names in background
					forkJoin({
						groups: this.scimAdminService.getGroups(),
						managedPolicies: this.scimAdminService.getPolicies(),
						orgPolicies: this.scimAdminService.getOrganizationPolicies(),
						accountPolicies: this.scimAdminService.getAccountPolicies(),
					})
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: ({ groups, managedPolicies, orgPolicies, accountPolicies }) => {
								this.cachedGroups = groups;

								const allPoliciesMap = new Map<string, any>();
								managedPolicies.forEach((policy) => allPoliciesMap.set(policy.id, policy));
								orgPolicies.forEach((policy) => {
									if (!allPoliciesMap.has(policy.id)) allPoliciesMap.set(policy.id, policy);
								});
								accountPolicies.forEach((policy) => {
									if (!allPoliciesMap.has(policy.id)) allPoliciesMap.set(policy.id, policy);
								});
								this.cachedPolicies = Array.from(allPoliciesMap.values());
								this.isDataReadyForCreatePolicyMap = true;

								const groupByExternalId = new Map(groups.map((group) => [group.externalId, group.displayName || group.id]));
								const groupById = new Map(groups.map((group) => [group.id, group.displayName || group.id]));
								const policyNameMap = new Map(this.cachedPolicies.map((policy) => [policy.id, policy.name]));

								this.policyMapsTableSource.data = policyMaps.map((policyMap) => ({
									...policyMap,
									groupName: groupByExternalId.get(policyMap.groupExternalId) || groupById.get(policyMap.groupExternalId) || policyMap.groupExternalId || '',
									policyName: policyNameMap.get(policyMap.policyId) || policyMap.policyId || '',
									createdAtFormatted: policyMap.createdAt ? aciesDateFormat(new Date(policyMap.createdAt), true) : '',
								}));
							},
						});
				},
				error: () => {
					this.isLoadingPolicyMaps = false;
					this.notificationService.notify(['scim-administration.errors.loadPolicyMaps.header'], ['scim-administration.errors.loadPolicyMaps.content'], 'error');
				},
			});
	}

	loadToken(): void {
		this.isLoadingToken = true;
		this.scimAdminService
			.getToken()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.isLoadingToken = false;
					this.dialog.open(ScimTokenDialogComponent, {
						width: '650px',
						data: response.token,
					});
				},
				error: () => {
					this.isLoadingToken = false;
					this.notificationService.notify(['scim-administration.errors.loadToken.header'], ['scim-administration.errors.loadToken.content'], 'error');
				},
			});
	}

	applyFilter(event: Event): void {
		const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
		this.searchValue = filterValue;
		switch (this.selectedTabIndex) {
			case 0:
				this.usersTableSource.filter = filterValue;
				break;
			case 1:
				this.groupsTableSource.filter = filterValue;
				break;
			case 2:
				this.policyMapsTableSource.filter = filterValue;
				break;
			case 3:
				this.policiesTableSource.filter = filterValue;
				break;
		}
	}

	handleRowMenuAction(object: object): void {
		const row = object as IRowAction;
		const handler: ActionHandler = row.action.handler;
		switch (handler) {
			case 'openUserDetail':
				this.openUserDetail(row.data as IScimUser);
				break;
			case 'confirmDeleteUser':
				this.confirmDeleteUser(row.data as IScimUser);
				break;
			case 'openGroupDetail':
				this.openGroupDetail(row.data as IScimGroup);
				break;
			case 'confirmDeleteGroup':
				this.confirmDeleteGroup(row.data as IScimGroup);
				break;
			case 'openPolicyMapDetail':
				this.openPolicyMapDetail(row.data as IPolicyMap);
				break;
			case 'confirmDeletePolicyMap':
				this.confirmDeletePolicyMap(row.data as IPolicyMap);
				break;
			case 'openPolicyDetail':
				this.openPolicyDetail(row.data as IPolicy);
				break;
			case 'editPolicy':
				this.editPolicy(row.data as IPolicy);
				break;
			case 'confirmDeletePolicy':
				this.confirmDeletePolicy(row.data as IPolicy);
				break;
			default:
				throw new Error(`Unknown row action handler: ${row.action.handler}`);
		}
	}

	openUserDetail(user: IScimUser): void {
		this.scimAdminService
			.getUser(user.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (userDetail) => {
					this.dialog.open(ScimUserDetailDialogComponent, {
						width: '650px',
						data: userDetail,
					});
				},
				error: () => {
					this.notificationService.notify(['scim-administration.errors.loadUserDetail.header'], ['scim-administration.errors.loadUserDetail.content'], 'error');
				},
			});
	}

	confirmDeleteUser(user: IScimUser): void {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('scim-administration.users.delete.title'),
					content: this.translator.instant('scim-administration.users.delete.confirm', {
						name: user.displayName || user.userName || user.id,
					}),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.scimAdminService
						.deleteUser(user.id)
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: () => {
								this.usersTableSource.data = this.usersTableSource.data.filter((existingUser) => existingUser.id !== user.id);
								this.notificationService.notify(
									['scim-administration.notifications.deleteUser.header'],
									[
										'scim-administration.notifications.deleteUser.content',
										{
											name: user.displayName || user.userName || user.id,
										},
									],
									'success',
								);
							},
							error: (error) => {
								this.notificationService.notify(['scim-administration.errors.deleteUser.header'], [error?.error?.message || 'scim-administration.errors.deleteUser.content'], 'error');
							},
						});
				}
			});
	}

	openGroupDetail(group: IScimGroup): void {
		this.scimAdminService
			.getGroup(group.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (groupDetail) => {
					this.dialog
						.open(ScimGroupDetailDialogComponent, {
							width: '650px',
							data: groupDetail,
						})
						.afterClosed()
						.pipe(takeUntil(this.destroy$))
						.subscribe((result) => {
							if (result) {
								this.loadGroups();
							}
						});
				},
				error: () => {
					this.notificationService.notify(['scim-administration.errors.loadGroupDetail.header'], ['scim-administration.errors.loadGroupDetail.content'], 'error');
				},
			});
	}

	confirmDeleteGroup(group: IScimGroup): void {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('scim-administration.groups.delete.title'),
					content: this.translator.instant('scim-administration.groups.delete.confirm', {
						name: group.displayName || group.id,
					}),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.scimAdminService
						.deleteGroup(group.id)
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: () => {
								this.groupsTableSource.data = this.groupsTableSource.data.filter((existingGroup) => existingGroup.id !== group.id);
								this.notificationService.notify(['scim-administration.notifications.deleteGroup.header'], ['scim-administration.notifications.deleteGroup.content', { name: group.displayName || group.id }], 'success');
							},
							error: (error) => {
								this.notificationService.notify(['scim-administration.errors.deleteGroup.header'], [error?.error?.message || 'scim-administration.errors.deleteGroup.content'], 'error');
							},
						});
				}
			});
	}

	openCreatePolicyMap(): void {
		this.dialog
			.open(CreatePolicyMapDialogComponent, {
				disableClose: true,
				width: '650px',
				data: {
					groups: this.cachedGroups,
					policies: this.cachedPolicies,
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((createdPolicyMap: IPolicyMap) => {
				if (createdPolicyMap) {
					this.loadPolicyMaps();
				}
			});
	}

	openPolicyMapDetail(policyMap: IPolicyMap): void {
		this.scimAdminService
			.getPolicyMap(policyMap.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (policyMapDetail) => {
					this.dialog.open(PolicyMapDetailDialogComponent, {
						width: '650px',
						data: policyMapDetail,
					});
				},
				error: () => {
					this.notificationService.notify(['scim-administration.errors.loadPolicyMapDetail.header'], ['scim-administration.errors.loadPolicyMapDetail.content'], 'error');
				},
			});
	}

	confirmDeletePolicyMap(policyMap: IPolicyMap): void {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('scim-administration.policyMaps.delete.title'),
					content: this.translator.instant('scim-administration.policyMaps.delete.confirm', {
						name: (policyMap as any).groupName || policyMap.groupExternalId || policyMap.id,
					}),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.scimAdminService
						.deletePolicyMap(policyMap.id)
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: () => {
								this.policyMapsTableSource.data = this.policyMapsTableSource.data.filter((existingPolicyMap) => existingPolicyMap.id !== policyMap.id);
								this.notificationService.notify(
									['scim-administration.notifications.deletePolicyMap.header'],
									[
										'scim-administration.notifications.deletePolicyMap.content',
										{
											name: (policyMap as any).groupName || policyMap.groupExternalId || policyMap.id,
										},
									],
									'success',
								);
							},
							error: (error) => {
								this.notificationService.notify(['scim-administration.errors.deletePolicyMap.header'], [error?.error?.message || 'scim-administration.errors.deletePolicyMap.content'], 'error');
							},
						});
				}
			});
	}

	// Policies tab
	loadPolicies(): void {
		this.isLoadingPolicies = true;
		forkJoin({
			managedPolicies: this.scimAdminService.getPolicies(),
			orgPolicies: this.scimAdminService.getOrganizationPolicies(),
			accountPolicies: this.scimAdminService.getAccountPolicies(),
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ managedPolicies, orgPolicies, accountPolicies }) => {
					const allPoliciesMap = new Map<string, any>();
					managedPolicies.forEach((policy) =>
						allPoliciesMap.set(policy.id, {
							...policy,
							type: policy.type || 'managed',
						}),
					);
					orgPolicies.forEach((policy) => {
						if (!allPoliciesMap.has(policy.id)) {
							allPoliciesMap.set(policy.id, {
								...policy,
								type: policy.type || 'organization',
							});
						}
					});
					accountPolicies.forEach((policy) => {
						if (!allPoliciesMap.has(policy.id)) {
							allPoliciesMap.set(policy.id, {
								...policy,
								type: policy.type || 'account',
							});
						}
					});
					this.policiesTableSource.data = Array.from(allPoliciesMap.values());
					this.isLoadingPolicies = false;
				},
				error: () => {
					this.isLoadingPolicies = false;
					this.notificationService.notify(['scim-administration.policies.errors.loadPolicies.header'], ['scim-administration.policies.errors.loadPolicies.content'], 'error');
				},
			});
	}

	openPolicyDetail(policy: IPolicy): void {
		this.dialog.open(PolicyDetailDialogComponent, {
			width: '650px',
			data: policy,
		});
	}

	editPolicy(policy: IPolicy): void {
		this.dialog
			.open(UpdatePolicyDialogComponent, {
				disableClose: true,
				width: '650px',
				data: policy,
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((updatedPolicy) => {
				if (updatedPolicy) {
					this.loadPolicies();
				}
			});
	}

	openCreatePolicy(): void {
		this.dialog
			.open(CreatePolicyDialogComponent, {
				disableClose: true,
				width: '650px',
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((createdPolicy) => {
				if (createdPolicy) {
					this.loadPolicies();
				}
			});
	}

	confirmDeletePolicy(policy: IPolicy): void {
		this.dialog
			.open(ConfirmdialogComponent, {
				width: '450px',
				data: {
					okReturnData: true,
					cancelReturnData: false,
					heading: this.translator.instant('scim-administration.policies.delete.title'),
					content: this.translator.instant('scim-administration.policies.delete.confirm', { name: policy.name }),
					okButtonText: this.translator.instant('general.ok'),
					cancelButtonText: this.translator.instant('general.cancel'),
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					const request$ = policy.type === 'organization' ? this.scimAdminService.deleteOrganizationPolicy(policy.id) : this.scimAdminService.deleteAccountPolicy(policy.id);

					request$.pipe(takeUntil(this.destroy$)).subscribe({
						next: () => {
							this.policiesTableSource.data = this.policiesTableSource.data.filter((existingPolicy) => existingPolicy.id !== policy.id);
							this.notificationService.notify(['scim-administration.policies.notifications.deleteSuccess.header'], ['scim-administration.policies.notifications.deleteSuccess.content', { name: policy.name }], 'success');
						},
						error: (error) => {
							this.notificationService.notify(['scim-administration.policies.notifications.deleteError.header'], [error?.error?.error?.message || 'scim-administration.policies.notifications.deleteError.content'], 'error');
						},
					});
				}
			});
	}
}
